import { useEffect, useState } from 'react';
import {
  ArrowClockwiseIcon,
  CheckCircleIcon,
  CircleNotchIcon,
  ClipboardTextIcon,
  CopyIcon,
  KeyIcon,
  PulseIcon,
  RobotIcon,
  ShieldWarningIcon,
  TrashIcon,
  WarningCircleIcon,
  XCircleIcon,
} from '@phosphor-icons/react';
import { PageContainer } from '@/components/layout/PageContainer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { env } from '@/utils/env';
import {
  mcpService,
  type McpConnection,
  type McpApiKey,
  type CreateApiKeyDto,
} from '@/services/integrations/mcp.service';
import { formatDistanceToNow } from 'date-fns';
import { createLogger } from '@/lib/logger';
import { formatDateUserTz } from '@/utils/date';

const logger = createLogger('pages/settings/mcp');

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const MCP_STREAM_URL = `${env.apiUrl}/integrations/mcp/stream`;

type ClientTab = 'claude' | 'chatgpt' | 'cursor' | 'windsurf' | 'vscode' | 'jetbrains' | 'openclaw' | 'stdio';

function buildConfigSnippet(tab: ClientTab, apiKey: string): string {
  const key = apiKey || 'YOUR_API_KEY_HERE';
  switch (tab) {
    case 'claude':
      return JSON.stringify(
        {
          mcpServers: {
            scrambl: {
              url: MCP_STREAM_URL,
            },
          },
        },
        null,
        2,
      );
    case 'chatgpt':
      return JSON.stringify(
        {
          mcpServers: {
            scrambl: {
              url: MCP_STREAM_URL,
            },
          },
        },
        null,
        2,
      );
    case 'cursor':
      return JSON.stringify(
        {
          mcpServers: {
            scrambl: {
              url: MCP_STREAM_URL,
              headers: { 'x-api-key': key },
            },
          },
        },
        null,
        2,
      );
    case 'windsurf':
      return JSON.stringify(
        {
          mcpServers: {
            scrambl: {
              serverUrl: MCP_STREAM_URL,
              headers: { 'x-api-key': key },
            },
          },
        },
        null,
        2,
      );
    case 'vscode':
      return JSON.stringify(
        {
          servers: {
            scrambl: {
              type: 'http',
              url: MCP_STREAM_URL,
              headers: { 'x-api-key': key },
            },
          },
        },
        null,
        2,
      );
    case 'jetbrains':
      return JSON.stringify(
        {
          mcpServers: {
            scrambl: {
              url: MCP_STREAM_URL,
              headers: { 'x-api-key': key },
            },
          },
        },
        null,
        2,
      );
    case 'openclaw':
      return JSON.stringify(
        {
          url: MCP_STREAM_URL,
          transport: 'streamable-http',
          headers: { 'x-api-key': key },
        },
        null,
        2,
      );
    case 'stdio':
      return JSON.stringify(
        {
          mcpServers: {
            scrambl: {
              command: 'npx',
              args: ['@scrambl/mcp-server'],
              env: {
                SCRAMBL_API_URL: env.apiUrl,
                SCRAMBL_API_KEY: key,
              },
            },
          },
        },
        null,
        2,
      );
  }
}

const CLIENT_TABS: { value: ClientTab; label: string }[] = [
  { value: 'chatgpt', label: 'ChatGPT' },
  { value: 'claude', label: 'Claude Desktop' },
  { value: 'cursor', label: 'Cursor' },
  { value: 'windsurf', label: 'Windsurf' },
  { value: 'vscode', label: 'VS Code Copilot' },
  { value: 'jetbrains', label: 'JetBrains' },
  { value: 'openclaw', label: 'OpenClaw' },
  { value: 'stdio', label: 'Local (stdio)' },
];

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function CopyButton({ text, label }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Button variant="ghost" size="sm" onClick={handleCopy} className="gap-1.5">
      {copied ? (
        <CheckCircleIcon className="h-4 w-4 text-green-500" />
      ) : (
        <CopyIcon className="h-4 w-4" />
      )}
      {label && <span className="text-xs">{label}</span>}
    </Button>
  );
}

function CodeBlock({ code, className }: { code: string; className?: string }) {
  return (
    <div className={cn('relative group', className)}>
      <pre className="p-4 rounded-lg bg-zinc-900 text-zinc-100 text-sm font-mono overflow-x-auto leading-relaxed">
        <code>{code}</code>
      </pre>
      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <CopyButton text={code} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Connection Status Section
// ---------------------------------------------------------------------------

function ConnectionStatus({
  connection,
  onEnable,
  enabling,
}: {
  connection: McpConnection | null;
  onEnable: () => void;
  enabling: boolean;
}) {
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<'success' | 'error' | null>(null);

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      await mcpService.getCapabilities();
      setTestResult('success');
      toast.success('Connection successful', {
        description: 'MCP server is responding correctly.',
      });
    } catch {
      setTestResult('error');
      toast.error('Connection failed', {
        description: 'Could not reach the MCP server. Check your configuration.',
      });
    } finally {
      setTesting(false);
    }
  };

  if (!connection) {
    return (
      <div className="flex flex-col gap-3 rounded-md border border-dashed border-border/60 bg-muted/15 p-4">
        <div className="flex items-start gap-3">
          <div className="flex h-8 w-8 flex-none items-center justify-center rounded-md bg-primary/10 text-primary">
            <RobotIcon className="h-4 w-4" weight="fill" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium">MCP integration is off</p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              The Model Context Protocol lets AI assistants like Claude, Cursor, and
              others read and manage your Scrambl data via a secure API.
            </p>
          </div>
        </div>
        <Button size="sm" onClick={onEnable} disabled={enabling} className="self-start">
          {enabling ? (
            <>
              <CircleNotchIcon className="mr-2 h-4 w-4 animate-spin" />
              Enabling…
            </>
          ) : (
            'Enable MCP integration'
          )}
        </Button>
      </div>
    );
  }

  const statusBadge = testResult === 'error'
    ? { label: 'Error', className: 'border-destructive/30 bg-destructive/5 text-destructive', icon: XCircleIcon }
    : { label: testResult === 'success' ? 'Connected' : 'Active', className: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/40 dark:bg-emerald-900/20 dark:text-emerald-400', icon: CheckCircleIcon };
  const StatusIcon = statusBadge.icon;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <Label className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
          Server URL
        </Label>
        <Badge
          variant="outline"
          className={cn('h-5 rounded-md px-1.5 text-[11px] font-normal', statusBadge.className)}
        >
          <StatusIcon className="mr-1 h-3 w-3" weight="fill" />
          {statusBadge.label}
        </Badge>
      </div>
      <div className="flex items-center gap-2">
        <code className="flex-1 truncate rounded-md border border-border/60 bg-muted/30 px-2.5 py-2 font-mono text-xs">
          {MCP_STREAM_URL}
        </code>
        <CopyButton text={MCP_STREAM_URL} />
      </div>
      <Button variant="outline" size="sm" onClick={testConnection} disabled={testing}>
        {testing ? (
          <>
            <CircleNotchIcon className="mr-2 h-3.5 w-3.5 animate-spin" />
            Testing…
          </>
        ) : (
          <>
            <ArrowClockwiseIcon className="mr-2 h-3.5 w-3.5" />
            Test connection
          </>
        )}
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Quick Connect Section
// ---------------------------------------------------------------------------

function QuickConnect({ activeApiKey }: { activeApiKey: string }) {
  const [selectedTab, setSelectedTab] = useState<ClientTab>('claude');
  const snippet = buildConfigSnippet(selectedTab, activeApiKey);

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        Copy a configuration snippet for your AI client. Your API key is auto-filled if available.
      </p>

      {/* Tab selector */}
      <div className="flex flex-wrap gap-1 rounded-md border border-border/60 bg-muted/20 p-1">
        {CLIENT_TABS.map((tab) => (
          <button
            key={tab.value}
            type="button"
            onClick={() => setSelectedTab(tab.value)}
            className={cn(
              'rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
              selectedTab === tab.value
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <CodeBlock code={snippet} />

      {(selectedTab === 'chatgpt' || selectedTab === 'claude') && (
        <Alert variant="default">
          <CheckCircleIcon className="h-4 w-4" />
          <AlertDescription className="text-xs">
            {selectedTab === 'chatgpt' ? 'ChatGPT' : 'Claude Desktop'} connects via OAuth — paste
            the URL and you'll be prompted to sign in. No API key needed.
          </AlertDescription>
        </Alert>
      )}

      {selectedTab !== 'chatgpt' && selectedTab !== 'claude' && !activeApiKey && (
        <Alert variant="default">
          <WarningCircleIcon className="h-4 w-4" />
          <AlertDescription className="text-xs">
            Create an API key below to auto-fill the config snippet.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// API Keys Section
// ---------------------------------------------------------------------------

function ApiKeys({
  apiKeys,
  onCreateKey,
  onRevokeKey,
  createdKey,
  onDismissCreatedKey,
}: {
  apiKeys: McpApiKey[];
  onCreateKey: (name: string) => Promise<void>;
  onRevokeKey: (id: string) => Promise<void>;
  createdKey: string | null;
  onDismissCreatedKey: () => void;
}) {
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [keyName, setKeyName] = useState('');
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState<string | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState<string | null>(null);

  const handleCreate = async () => {
    if (!keyName.trim()) return;
    setCreating(true);
    try {
      await onCreateKey(keyName.trim());
      setKeyName('');
      setShowCreateDialog(false);
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (id: string) => {
    setRevoking(id);
    try {
      await onRevokeKey(id);
    } finally {
      setRevoking(null);
      setConfirmRevoke(null);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">
          <ShieldWarningIcon className="mr-1 inline h-3 w-3" weight="fill" />
          Keys provide full access to your workspace. Keep them secure.
        </p>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button size="sm" variant="outline" className="h-7 gap-1 px-2 text-xs">
              <KeyIcon className="h-3.5 w-3.5" />
              New key
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create API key</DialogTitle>
              <DialogDescription>
                Give your key a name so you can identify it later.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div className="space-y-2">
                <Label htmlFor="new-key-name">Name</Label>
                <Input
                  id="new-key-name"
                  placeholder="e.g., Claude Desktop, Cursor, My Script"
                  value={keyName}
                  onChange={(e) => setKeyName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                  autoFocus
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreate} disabled={creating || !keyName.trim()}>
                {creating ? (
                  <>
                    <CircleNotchIcon className="mr-2 h-4 w-4 animate-spin" />
                    Creating…
                  </>
                ) : (
                  'Create key'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Newly created key banner */}
      {createdKey && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 dark:border-emerald-900/40 dark:bg-emerald-900/20">
          <div className="mb-2 flex items-center gap-1.5 text-xs font-medium text-emerald-900 dark:text-emerald-300">
            <KeyIcon className="h-3.5 w-3.5" weight="fill" />
            New API key created
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 break-all rounded bg-background px-2 py-1.5 font-mono text-xs">
              {createdKey}
            </code>
            <CopyButton text={createdKey} />
          </div>
          <div className="mt-2 flex items-center justify-between">
            <p className="flex items-center gap-1 text-[11px] text-emerald-800 dark:text-emerald-400">
              <WarningCircleIcon className="h-3 w-3" />
              Copy now — won't be shown again.
            </p>
            <Button variant="ghost" size="sm" onClick={onDismissCreatedKey} className="h-6 px-2 text-xs">
              Dismiss
            </Button>
          </div>
        </div>
      )}

      {/* Key list */}
      {apiKeys.length === 0 ? (
        <div className="rounded-md border border-dashed border-border/60 px-4 py-6 text-center">
          <KeyIcon className="mx-auto mb-1.5 h-5 w-5 text-muted-foreground/60" />
          <p className="text-xs font-medium">No API keys yet</p>
          <p className="text-[11px] text-muted-foreground">Create one to get started.</p>
        </div>
      ) : (
        <ul className="overflow-hidden rounded-md border border-border/60 divide-y divide-border/60">
          {apiKeys.map((key) => (
            <li
              key={key.id}
              className="group flex items-center gap-3 px-3 py-2.5 transition-colors hover:bg-muted/20"
            >
              <div className="flex h-7 w-7 flex-none items-center justify-center rounded-md bg-muted text-muted-foreground">
                <KeyIcon className="h-3.5 w-3.5" weight="fill" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-sm font-medium">{key.name}</span>
                  <code className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground">
                    {key.keyPrefix}…
                  </code>
                  {key.scopes?.map((s) => (
                    <Badge key={s} variant="outline" className="h-4 rounded px-1 text-[10px] font-normal">
                      {s}
                    </Badge>
                  ))}
                </div>
                <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
                  <span>Created {formatDateUserTz(key.createdAt, 'MMM d, yyyy')}</span>
                  {key.lastUsedAt && (
                    <>
                      <span className="text-border">·</span>
                      <span>Last used {formatDistanceToNow(new Date(key.lastUsedAt))} ago</span>
                    </>
                  )}
                </div>
              </div>
              <Dialog
                open={confirmRevoke === key.id}
                onOpenChange={(open) => !open && setConfirmRevoke(null)}
              >
                <DialogTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`Revoke ${key.name}`}
                    onClick={() => setConfirmRevoke(key.id)}
                    disabled={revoking === key.id}
                    className="h-7 w-7 p-0 text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100 focus-visible:opacity-100"
                  >
                    <TrashIcon className="h-3.5 w-3.5" />
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Revoke API key</DialogTitle>
                    <DialogDescription>
                      Are you sure you want to revoke <strong>{key.name}</strong>? Any AI
                      clients using this key will immediately lose access.
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setConfirmRevoke(null)}>
                      Cancel
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={() => handleRevoke(key.id)}
                      disabled={revoking === key.id}
                    >
                      {revoking === key.id ? 'Revoking…' : 'Revoke key'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Usage Metrics Section
// ---------------------------------------------------------------------------

function UsageMetrics({ connection }: { connection: McpConnection | null }) {
  const [metrics, setMetrics] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!connection) return;
    setLoading(true);
    mcpService
      .getMetrics(24)
      .then(setMetrics)
      .catch(() => null)
      .finally(() => setLoading(false));
  }, [connection]);

  if (!connection) return null;

  if (loading) {
    return (
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-16 w-full rounded-md" />
        ))}
      </div>
    );
  }

  if (!metrics) {
    return (
      <p className="py-2 text-center text-xs text-muted-foreground">
        No usage data yet. Metrics appear once your AI clients start making requests.
      </p>
    );
  }

  const successRate =
    metrics.totalRequests > 0
      ? `${Math.round(((metrics.successfulRequests ?? 0) / metrics.totalRequests) * 100)}%`
      : '—';

  return (
    <div className="grid grid-cols-2 divide-x divide-y divide-border/60 overflow-hidden rounded-md border border-border/60 md:grid-cols-4 md:divide-y-0">
      <Metric label="Total requests" value={metrics.totalRequests ?? 0} />
      <Metric
        label="Success rate"
        value={successRate}
        sub={metrics.failedRequests ? `${metrics.failedRequests} failed` : undefined}
      />
      <Metric
        label="Avg response"
        value={metrics.averageResponseTime != null ? `${metrics.averageResponseTime}ms` : '—'}
      />
      <Metric label="Active keys" value={metrics.activeApiKeys ?? 0} />
    </div>
  );
}

function Metric({
  label,
  value,
  sub,
}: {
  label: string;
  value: string | number;
  sub?: string;
}) {
  return (
    <div className="bg-muted/15 px-3 py-2.5">
      <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <p className="mt-0.5 text-xl font-semibold">{value}</p>
      {sub && <p className="text-[11px] text-muted-foreground">{sub}</p>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Reusable MCP Settings Content (no PageContainer wrapper)
// ---------------------------------------------------------------------------

export function McpSettingsContent() {
  const [connection, setConnection] = useState<McpConnection | null>(null);
  const [apiKeys, setApiKeys] = useState<McpApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [enabling, setEnabling] = useState(false);
  const [createdKey, setCreatedKey] = useState<string | null>(null);

  // Derive first active API key for config snippets
  // If we just created a key, use that; otherwise the prefix hint (full key not available after creation)
  const activeApiKey = createdKey ?? '';

  const loadData = async () => {
    try {
      setLoading(true);
      const [conn, keys] = await Promise.all([
        mcpService.getConnection().catch(() => null),
        mcpService.listApiKeys().catch(() => []),
      ]);
      setConnection(conn);
      setApiKeys(keys);

    } catch (error) {
      logger.error('Failed to load MCP data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const enableMcp = async () => {
    try {
      setEnabling(true);
      const newConn = await mcpService.createConnection({
        name: 'MCP Integration',
        description: 'Model Context Protocol integration for AI assistants',
      });
      setConnection(newConn);
      toast.success('MCP enabled', {
        description: 'You can now generate API keys and connect AI assistants.',
      });
    } catch (error) {
      if (error instanceof Error && 'status' in error && (error as { status: number }).status === 403) {
        toast.error('Permission denied', {
          description: 'You do not have permission to enable integrations. Please contact your admin.',
        });
      } else {
        toast.error('Failed to enable MCP');
      }
    } finally {
      setEnabling(false);
    }
  };

  const handleCreateKey = async (name: string) => {
    try {
      const data: CreateApiKeyDto = {
        name,
        scopes: ['read', 'write'],
        connectionId: connection?.id,
      };
      const result = await mcpService.createApiKey(data);
      setCreatedKey(result.key || null);
      setApiKeys((prev) => [result, ...prev]);
      toast.success('API key created', {
        description: "Copy it now — it won't be shown again!",
      });
    } catch (error) {
      if (error instanceof Error && 'status' in error && (error as { status: number }).status === 403) {
        toast.error('Permission denied', {
          description: 'You do not have permission to manage integrations. Please contact your admin.',
        });
      } else {
        toast.error('Failed to create API key');
      }
    }
  };

  const handleRevokeKey = async (id: string) => {
    try {
      await mcpService.revokeApiKey(id);
      setApiKeys((prev) => prev.filter((k) => k.id !== id));
      toast.success('API key revoked');
    } catch (error) {
      if (error instanceof Error && 'status' in error && (error as { status: number }).status === 403) {
        toast.error('Permission denied', {
          description: 'You do not have permission to manage integrations. Please contact your admin.',
        });
      } else {
        toast.error('Failed to revoke API key');
      }
    }
  };

  if (loading) {
    return (
      <section className="overflow-hidden rounded-xl border border-border/60 bg-card">
        <div className="h-9 animate-pulse border-b border-border/60 bg-muted/40" />
        <div className="space-y-3 p-4">
          <Skeleton className="h-4 w-3/5" />
          <Skeleton className="h-20 w-full rounded-md" />
        </div>
      </section>
    );
  }

  return (
    <section className="overflow-hidden rounded-xl border border-border/60 bg-card">
      <header className="flex items-center gap-1.5 border-b border-border/60 bg-muted/40 px-4 py-2">
        <RobotIcon className="h-3.5 w-3.5 text-muted-foreground" weight="fill" />
        <h3 className="text-sm font-semibold">AI assistants</h3>
      </header>

      <div className="p-4">
        <p className="mb-3 text-xs text-muted-foreground">
          Connect AI assistants like Claude, Cursor, and OpenClaw using the Model
          Context Protocol (MCP).
        </p>
        <ConnectionStatus connection={connection} onEnable={enableMcp} enabling={enabling} />
      </div>

      {connection && (
        <div className="divide-y divide-border/60 border-t border-border/60">
          {/* Quick Connect */}
          <div>
            <SubsectionBand icon={ClipboardTextIcon} label="Quick connect" />
            <div className="px-4 py-3">
              <QuickConnect activeApiKey={activeApiKey} />
            </div>
          </div>

          {/* API Keys */}
          <div>
            <SubsectionBand icon={KeyIcon} label="API keys" count={apiKeys.length} />
            <div className="px-4 py-3">
              <ApiKeys
                apiKeys={apiKeys}
                onCreateKey={handleCreateKey}
                onRevokeKey={handleRevokeKey}
                createdKey={createdKey}
                onDismissCreatedKey={() => setCreatedKey(null)}
              />
            </div>
          </div>

          {/* Usage Metrics */}
          <div>
            <SubsectionBand icon={PulseIcon} label="Usage (last 24h)" />
            <div className="px-4 py-3">
              <UsageMetrics connection={connection} />
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function SubsectionBand({
  icon: Icon,
  label,
  count,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  count?: number;
}) {
  return (
    <div className="flex items-center gap-1.5 bg-muted/15 px-4 py-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
      <Icon className="h-3 w-3" />
      <span>{label}</span>
      {count !== undefined && count > 0 && (
        <span className="rounded bg-background px-1.5 text-[10px] font-normal normal-case tracking-normal">
          {count}
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Standalone Page (with PageContainer wrapper)
// ---------------------------------------------------------------------------

export default function McpSettingsPage() {
  return (
    <PageContainer
      header={{
        title: 'AI Assistant Integration',
        description: 'Connect AI tools to your Scrambl workspace via MCP',
      }}
    >
      <McpSettingsContent />
    </PageContainer>
  );
}
